import type { SelectOption } from '@brighterly/lib-lumee-ui'
import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { trackEvent } from '@shared/lib/amplitude'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { useLandingStore } from './store'

dayjs.extend(utc)
dayjs.extend(timezone)

interface SelectLessonDatePayload {
  disciplineCode: ComputedRef<Discipline>
}

interface SelectLessonDateReturn {
  options: Ref<SelectOption[]>
  setChangeDate: (value: string) => void
}

const getDateOptions = (dates: string[]): SelectOption[] => dates.map(date => ({
  value: date,
  text: dayjs(date, DateFormat.DefaultDate).format('ddd, MMMM DD'),
}))

export const useSelectLessonDate = (payload: SelectLessonDatePayload): SelectLessonDateReturn => {
  const landingStore = useLandingStore()

  const getOptions = (): SelectOption[] => {
    const dates = landingStore.getAvailableDates
    const options = getDateOptions(dates)

    return options
  }

  const options = ref<SelectOption[]>(getOptions())

  const setChangeDate = (value: string): void => {
    // The landing's subject, not the active discipline (spec D8).
    trackEvent('Field_change', {
      feature: 'landing',
      name: 'lesson_date',
      value,
      discipline_code: toAnalyticsId(payload.disciplineCode.value),
    })

    landingStore.setSelectedDate(value)
  }

  const setRecalculateOptions = (): void => {
    options.value = getOptions()
  }

  watch(() => landingStore.availableSlots, setRecalculateOptions, { immediate: true })

  return {
    options,
    setChangeDate,
  }
}
